/**
 * Audit API client (P2-S8).
 *
 * /api/v1/admin/audit-logs is already exposed by admin router; this client
 * adds typed helpers + CSV download URL builder.
 */
import { api } from "./client";

export interface AuditLogItem {
  id: string;
  user_id: string;
  action: string;
  target_model: string;
  target_id: string | null;
  before_state: Record<string, unknown> | null;
  after_state: Record<string, unknown> | null;
  ip_address: string | null;
  created_at: string;
}

export interface AuditLogList {
  items: AuditLogItem[];
  has_more: boolean;
}

export interface AuditDetail extends AuditLogItem {
  conversation_id: string | null;
  message_id: string | null;
  user_agent: string | null;
}

export interface AuditFilterParams {
  user_id?: string;
  target_model?: string;
  action?: string;
  days_back?: number;
  limit?: number;
}

function _toQuery(
  p: AuditFilterParams,
): Record<string, string | number | boolean | undefined> {
  return {
    user_id: p.user_id,
    target_model: p.target_model,
    action: p.action,
    days_back: p.days_back,
    limit: p.limit,
  };
}

export const auditApi = {
  list: (params: AuditFilterParams = {}) =>
    api.get<AuditLogList>("/api/v1/admin/audit-logs", { query: _toQuery(params) }),
  get: (id: string) => api.get<AuditDetail>(`/api/v1/admin/audit-logs/${id}`),
  /** Download CSV qua Bearer fetch + Blob → trigger browser download. */
  downloadCsv: async (
    params: AuditFilterParams = {},
    accessToken: string | null,
  ): Promise<void> => {
    const base = import.meta.env.VITE_API_BASE_URL ?? window.location.origin;
    const url = new URL("/api/v1/admin/audit-logs/export.csv", base);
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    }
    const headers: Record<string, string> = { Accept: "text/csv" };
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    const res = await fetch(url.toString(), {
      method: "GET",
      headers,
      credentials: "include",
    });
    if (!res.ok) {
      throw new Error(`Export failed: HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const dlUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = dlUrl;
    a.download = `audit_logs_${params.days_back ?? 30}d.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(dlUrl), 1000);
  },
};
